import React, { Suspense, useContext, useEffect, useRef, useState } from "react";
import {
  unstable_HistoryRouter as HistoryRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import ThemeProvider, { ThemeContext } from "../contexts/theme";
import UserProvider from "../contexts/user";
import { tokenManager } from "../services/auth";
import { appHistory } from "./history";
import { subscribePageTransition } from "./pageTransition";
import config from "../config/config";

import Loading from "../components/feedback/Loading";
import PageTransitionOverlay from "../components/layout/PageTransitionOverlay/PageTransitionOverlay";
// Lazy-loaded route components for code splitting
const Login = React.lazy(() => import("../views/auth/Login"));
const TwoFactorAuth = React.lazy(() => import("../views/auth/two-factor-auth"));
const ResetPassword = React.lazy(() => import("../views/auth/reset-password"));
const RecoverPassword = React.lazy(() => import("../views/auth/recover-password"));
const Dashboard = React.lazy(() => import("../views/dashboard"));
const LocationsMonitoring = React.lazy(() => import("../views/locations-monitoring"));
const Tariffs = React.lazy(() => import("../views/tariffs"));
const SmartCharging = React.lazy(() => import("../views/smart-charging"));
const PublicLayout = React.lazy(() => import("../layout/Public"));
const PublicCheckoutLayout = React.lazy(() => import("../layout/PublicCheckout"));
const PublicMinimalLayout = React.lazy(() => import("../layout/PublicMinimal"));
const LandingPage = React.lazy(() => import("../views/public/LandingPage"));
const PrivacyPolicy = React.lazy(() => import("../views/public/PrivacyPolicy"));
const CookiePolicy = React.lazy(() => import("../views/public/CookiePolicy"));
const TermsAndConditions = React.lazy(() => import("../views/public/TermsAndConditions"));
const PublicCheckout = React.lazy(() => import("../views/public/PublicCheckout"));
const PublicError = React.lazy(() => import("../views/public/PublicError"));
// FASE DE TESTES — página de acompanhamento da sessão ainda não está totalmente implementada.
const PublicSessionLayout = React.lazy(() => import("../layout/PublicSession"));
const PublicSessionTracking = React.lazy(() => import("../views/public/PublicSessionTracking"));

// React Query Configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

// Route Components
const DefaultLayout = React.lazy(() => import("../layout/Default"));

/**
 * Check if user has a valid token
 * @returns {boolean} True if token is valid and not expired
 */
const isAuthenticated = () => {
  const hasToken = tokenManager.hasValidToken();
  if (!hasToken) {
    tokenManager.clearToken();
  }
  return hasToken;
};

/**
 * Protected routes - require authentication
 */
const PrivateRoutes = () =>
  isAuthenticated() ? (
    <UserProvider>
      <Outlet />
    </UserProvider>
  ) : (
    <Navigate to="/login" replace />
  );

/**
 * Public routes - redirect to dashboard if already authenticated
 */
const PublicRoutes = () => (isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Outlet />);

/**
 * Root route handler - redirects based on authentication state
 * - Authenticated users → /dashboard
 * - Guest users → stay on landing page (rendered via route outlet)
 */
const RootRoute = () => {
  return <Outlet />;
};

const TRANSITION_DELAY = 2000;

/**
 * FASE DE TESTES — rota pública `/s/:token` da página de acompanhamento.
 * Ainda não está implementada.
 */
const isSessionTrackingPath = (pathname) => pathname === "/s" || pathname.startsWith("/s/");

/**
 * Layout Wrapper
 * Manages body class based on current route
 */
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const { setTheme, theme } = useContext(ThemeContext);

  useEffect(() => {
    const publicLightPaths = new Set([
      "/",
      "/privacy-policy",
      "/cookie-policy",
      "/terms-and-conditions",
      "/login",
      "/recover-password",
      "/reset-password",
      "/error",
    ]);

    const shouldForceLightTheme =
      (publicLightPaths.has(location.pathname) ||
        location.pathname.startsWith("/checkout/") ||
        isSessionTrackingPath(location.pathname)) &&
      !isAuthenticated();

    if (shouldForceLightTheme && theme !== config.theme.default) {
      setTheme(config.theme.default);
    }
  }, [location.pathname, setTheme, theme]);

  useEffect(() => {
    // Check if current route is a public page
    const isPublicPage =
      ["/", "/privacy-policy", "/cookie-policy", "/terms-and-conditions", "/error"].includes(location.pathname) ||
      location.pathname.startsWith("/checkout/") ||
      isSessionTrackingPath(location.pathname);

    if (isPublicPage) {
      document.body.classList.remove("dashboard-layout");
    } else {
      document.body.classList.add("dashboard-layout");
    }
  }, [location.pathname]);

  return children;
};

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimerRef = useRef(null);

  useEffect(() => {
    // Validate token on app mount
    isAuthenticated();
    setIsInitialized(true);

    // Check token validity every 60 seconds
    const interval = setInterval(() => {
      isAuthenticated();
    }, 60000);

    return () => {
      clearInterval(interval);
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isTransitioning) {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
      return;
    }

    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }

    transitionTimerRef.current = setTimeout(() => {
      setIsTransitioning(false);
      transitionTimerRef.current = null;
    }, TRANSITION_DELAY);

    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [isTransitioning]);

  useEffect(() => {
    const unsubscribe = subscribePageTransition(() => {
      setIsTransitioning(true);
    });

    return unsubscribe;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HistoryRouter history={appHistory}>
          <LayoutWrapper>
            <PageTransitionOverlay active={isTransitioning} />
            <Suspense fallback={<Loading />}>
              {!isInitialized ? (
                <Loading />
              ) : isTransitioning ? null : (
                <Routes>
                  {/* Reset password route - accessible regardless of auth state */}
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Auth routes - redirect to home if authenticated */}
                  <Route element={<PublicRoutes />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/recover-password" element={<RecoverPassword />} />
                    <Route path="/two-factor-auth" element={<TwoFactorAuth />} />
                  </Route>

                  {/* Root route - landing page for guests, dashboard redirect for authenticated users */}
                  <Route path="/" element={<RootRoute />}>
                    <Route element={<PublicLayout />}>
                      <Route index element={<LandingPage />} />
                    </Route>
                  </Route>

                  {/* Public routes with layout (accessible to all users) */}
                  <Route element={<PublicLayout />}>
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  </Route>
                  <Route element={<PublicCheckoutLayout />}>
                    <Route path="/checkout/:qr_token" element={<PublicCheckout />} />
                  </Route>
                  <Route element={<PublicMinimalLayout />}>
                    <Route path="/checkout/error" element={<PublicError />} />
                    <Route path="/error" element={<PublicError />} />
                  </Route>
                  {/* FASE DE TESTES — acompanhamento da sessão: mock, sem API. */}
                  <Route element={<PublicSessionLayout />}>
                    <Route path="/s/:token" element={<PublicSessionTracking />} />
                    <Route path="/s" element={<PublicSessionTracking />} />
                  </Route>

                  {/* Protected routes - require authentication */}
                  <Route element={<PrivateRoutes />}>
                    <Route path="/*" element={<DefaultLayout />}>
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="locations" element={<LocationsMonitoring />} />
                      <Route path="tariffs" element={<Tariffs />} />
                      <Route path="smart-charging" element={<SmartCharging />} />
                    </Route>

                    <Route path="*" element={<DefaultLayout />} />
                  </Route>
                </Routes>
              )}
            </Suspense>
          </LayoutWrapper>
        </HistoryRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
